"use client";

import { useRef, useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog-client";
import AnalyticsDashboard from "./dashboard";
import { ANALYTICS_COPY, ANALYTICS_VIEWS } from "./data";
import type { ProductVariant, ViewKey } from "./types";

interface Props {
  variant: ProductVariant;
}

export default function ProductAnalyticsSection({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const [view, setView] = useState<ViewKey>("trends");
  const tabRefs = useRef<Partial<Record<ViewKey, HTMLButtonElement | null>>>({});
  const copy = ANALYTICS_COPY[variant];

  function handleViewChange(next: ViewKey) {
    if (next === view) return;
    setView(next);
    trackEvent("analytics_feature_view_changed", { product: variant, view: next });
  }

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  /* Roving arrow-key navigation, which a tablist owes its keyboard users and
     which the previous vertical list never had. Horizontal now, so it is the
     left/right pair. */
  function handleTabKey(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = ANALYTICS_VIEWS[(index + delta + ANALYTICS_VIEWS.length) % ANALYTICS_VIEWS.length];
    handleViewChange(next.key);
    tabRefs.current[next.key]?.focus();
  }

  return (
    <section
      id="analytics-section"
      className="py-section border-border overflow-hidden border-b"
      aria-labelledby="analytics-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header sits on its own row, top-left, rather than in a column beside
            the dashboard — the visual gets the full container width that way. */}
        <motion.div {...fadeUp(0)} className="max-w-3xl">
          <SectionHeader
            eyebrow={copy.eyebrow}
            id="analytics-heading"
            heading={copy.heading}
            body={copy.body}
            headingClassName="mb-5"
          />
        </motion.div>

        {/* Above the panel, which is both the easier reach when switching views
            and the order a tablist is expected to be in — the tabs come before
            the tabpanel they control. */}
        <motion.div {...fadeUp(0.12)} className="mt-12 flex justify-center">
          <div
            className="border-border bg-foreground/[0.03] inline-flex flex-col gap-1 rounded-2xl border p-1.5 sm:flex-row sm:gap-1"
            role="tablist"
            aria-label="Analytics views"
            aria-orientation="horizontal"
          >
            {ANALYTICS_VIEWS.map((item, index) => {
              const active = view === item.key;
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  ref={(node) => {
                    tabRefs.current[item.key] = node;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls="analytics-dashboard-panel"
                  tabIndex={active ? 0 : -1}
                  onClick={() => handleViewChange(item.key)}
                  onKeyDown={(event) => handleTabKey(event, index)}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors",
                    active
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"
                  )}
                  style={
                    active
                      ? {
                          borderColor: "color-mix(in srgb, var(--brand-green) 35%, transparent)",
                          color: "var(--brand-green)",
                        }
                      : undefined
                  }
                >
                  <Icon size={16} aria-hidden="true" />
                  {item.title}
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.2)} className="mt-8">
          <AnalyticsDashboard view={view} variant={variant} accessPoints={copy.accessPoints} />
        </motion.div>
      </div>
    </section>
  );
}
